/**
 * Room Service - Handles all room-related operations
 * Including creation, joining, leaving, and real-time updates
 */

import { database } from './firebase';
import {
  ref,
  set,
  get,
  update,
  remove,
  onValue,
  off,
  serverTimestamp,
  onDisconnect
} from 'firebase/database';
import { generateRoomCode } from '../utils/codeGenerator';
import { notifyPlayerJoined, notifyPlayerReady, createNotification, NOTIFICATION_TYPES } from './notificationService';

/**
 * Creates a new game room
 *
 * @param {string} hostId - User ID of the room host
 * @param {string} hostName - Display name of the host
 * @param {string} hostPhoto - Photo URL of the host
 * @returns {Promise<string>} The generated room code
 */
export const createRoom = async (hostId, hostName, hostPhoto) => {
  try {
    // Generate a unique room code
    let roomCode = generateRoomCode();
    let isUnique = false;
    let attempts = 0;
    const maxAttempts = 10;

    // Ensure the room code is unique
    while (!isUnique && attempts < maxAttempts) {
      const roomRef = ref(database, `rooms/${roomCode}`);
      const snapshot = await get(roomRef);

      if (!snapshot.exists()) {
        isUnique = true;
      } else {
        roomCode = generateRoomCode();
        attempts++;
      }
    }

    if (!isUnique) {
      throw new Error('Failed to generate unique room code');
    }

    // Create the room in Firebase
    const roomRef = ref(database, `rooms/${roomCode}`);
    const roomData = {
      code: roomCode,
      host: {
        userId: hostId,
        displayName: hostName,
        photoURL: hostPhoto,
        ready: true
      },
      guest: null,
      gameSelected: null,
      status: 'waiting', // waiting, ready, playing, completed
      createdAt: serverTimestamp(),
      lastActivity: serverTimestamp()
    };

    await set(roomRef, roomData);

    // Rooms are now persistent for re-entry, so we don't delete on disconnect
    // Instead, we mark the room as inactive when players leave

    return roomCode;
  } catch (error) {
    console.error('Error creating room:', error);
    throw error;
  }
};

/**
 * Joins an existing room as a guest
 * Now allows re-joining inactive rooms
 *
 * @param {string} roomCode - The room code to join
 * @param {string} guestId - User ID of the guest
 * @param {string} guestName - Display name of the guest
 * @param {string} guestPhoto - Photo URL of the guest
 * @returns {Promise<void>}
 */
export const joinRoom = async (roomCode, guestId, guestName, guestPhoto) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const roomData = snapshot.val();

    // Check if room already has a guest (and it's not the same user rejoining)
    if (roomData.guest && roomData.guest.userId !== guestId) {
      throw new Error('Room is full');
    }

    // Allow joining waiting or inactive rooms (for re-entry)
    if (roomData.status !== 'waiting' && roomData.status !== 'inactive') {
      throw new Error('Room is not available');
    }

    // Add guest to the room
    const guestData = {
      userId: guestId,
      displayName: guestName,
      photoURL: guestPhoto,
      ready: true
    };

    // Reactivate room if it was inactive
    const newStatus = roomData.status === 'inactive' ? 'waiting' : roomData.status;

    await update(roomRef, {
      guest: guestData,
      status: newStatus,
      lastActivity: serverTimestamp()
    });

    // Set up automatic cleanup on guest disconnect
    const guestDisconnectRef = ref(database, `rooms/${roomCode}/guest`);
    onDisconnect(guestDisconnectRef).remove();

    // Player joined notification removed as per user request

  } catch (error) {
    console.error('Error joining room:', error);
    throw error;
  }
};

/**
 * Leaves a room (either as host or guest)
 * Rooms are now kept for re-entry instead of being deleted
 *
 * @param {string} roomCode - The room code
 * @param {string} userId - User ID of the person leaving
 * @returns {Promise<void>}
 */
export const leaveRoom = async (roomCode, userId) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) {
      return; // Room already deleted
    }

    const roomData = snapshot.val();

    // If host is leaving, mark room as inactive but keep it for re-entry
    if (roomData.host.userId === userId) {
      await update(roomRef, {
        guest: null,
        status: 'inactive',
        gameSelected: null,
        lastActivity: serverTimestamp()
      });
    }
    // If guest is leaving, just remove guest data
    else if (roomData.guest && roomData.guest.userId === userId) {
      const guestName = roomData.guest.displayName;

      await update(roomRef, {
        guest: null,
        status: roomData.status === 'playing' ? 'inactive' : 'waiting',
        gameSelected: null,
        lastActivity: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error leaving room:', error);
    throw error;
  }
};

/**
 * Sets the ready status for a player
 *
 * @param {string} roomCode - The room code
 * @param {string} userId - User ID
 * @param {boolean} ready - Ready status
 * @returns {Promise<void>}
 */
export const setPlayerReady = async (roomCode, userId, ready) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const roomData = snapshot.val();
    let recipientId = null;
    let playerName = null;

    // Update ready status based on whether user is host or guest
    if (roomData.host.userId === userId) {
      playerName = roomData.host.displayName;
      recipientId = roomData.guest ? roomData.guest.userId : null;

      await update(roomRef, {
        'host/ready': ready,
        lastActivity: serverTimestamp()
      });
    } else if (roomData.guest && roomData.guest.userId === userId) {
      playerName = roomData.guest.displayName;
      recipientId = roomData.host.userId;

      await update(roomRef, {
        'guest/ready': ready,
        lastActivity: serverTimestamp()
      });
    }

    // Notify the other player if they're ready and the other player exists
    if (ready && recipientId && playerName) {
      try {
        await notifyPlayerReady(recipientId, playerName, roomCode);
      } catch (notificationError) {
        console.error('Error sending ready notification:', notificationError);
      }
    }

    // Check if both players are ready
    const updatedSnapshot = await get(roomRef);
    const updatedData = updatedSnapshot.val();

    if (updatedData.host.ready && updatedData.guest && updatedData.guest.ready) {
      await update(roomRef, {
        status: 'ready'
      });
    }
  } catch (error) {
    console.error('Error setting player ready:', error);
    throw error;
  }
};

/**
 * Gets room data
 *
 * @param {string} roomCode - The room code
 * @returns {Promise<Object|null>} Room data or null if not found
 */
export const getRoom = async (roomCode) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }

    return null;
  } catch (error) {
    console.error('Error getting room:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time room updates
 *
 * @param {string} roomCode - The room code
 * @param {Function} callback - Callback function to handle updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToRoom = (roomCode, callback) => {
  const roomRef = ref(database, `rooms/${roomCode}`);

  const unsubscribe = onValue(
    roomRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.val());
      } else {
        callback(null); // Room was deleted
      }
    },
    (error) => {
      console.error('Error in room subscription:', error);
      callback(null);
    }
  );

  // Return unsubscribe function
  return () => off(roomRef, 'value', unsubscribe);
};

/**
 * Checks if a room exists
 *
 * @param {string} roomCode - The room code to check
 * @returns {Promise<boolean>} True if room exists, false otherwise
 */
export const roomExists = async (roomCode) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking room existence:', error);
    return false;
  }
};

/**
 * Updates the last activity timestamp for a room
 *
 * @param {string} roomCode - The room code
 * @returns {Promise<void>}
 */
export const updateRoomActivity = async (roomCode) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    await update(roomRef, {
      lastActivity: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating room activity:', error);
  }
};

/**
 * Kicks a player from the room (host only)
 *
 * @param {string} roomCode - The room code
 * @param {string} hostId - User ID of the host (for verification)
 * @returns {Promise<void>}
 */
export const kickGuest = async (roomCode, hostId) => {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }

    const roomData = snapshot.val();

    // Verify the requester is the host
    if (roomData.host.userId !== hostId) {
      throw new Error('Only the host can kick players');
    }

    // Remove the guest
    await update(roomRef, {
      guest: null,
      status: 'waiting',
      gameSelected: null,
      lastActivity: serverTimestamp()
    });
  } catch (error) {
    console.error('Error kicking guest:', error);
    throw error;
  }
};

/**
 * Invites a friend to a room by creating a room and sending an invite notification
 *
 * @param {string} hostId - User ID of the person sending the invite
 * @param {string} hostName - Display name of the host
 * @param {string} hostPhoto - Photo URL of the host
 * @param {string} friendId - User ID of the friend to invite
 * @param {string} friendName - Display name of the friend
 * @returns {Promise<string>} The created room code
 */
export const inviteFriendToRoom = async (hostId, hostName, hostPhoto, friendId, friendName) => {
  try {
    // Create a new room
    const roomCode = await createRoom(hostId, hostName, hostPhoto);

    // Send invite notification to friend
    await createNotification(friendId, {
      type: NOTIFICATION_TYPES.ROOM_INVITE,
      title: 'Room Invite',
      message: `${hostName} invited you to play!`,
      data: { roomCode, hostId, hostName },
      actionUrl: `/room/${roomCode}`
    });

    console.log(`Invite sent to ${friendName} for room ${roomCode}`);
    return roomCode;
  } catch (error) {
    console.error('Error inviting friend to room:', error);
    throw error;
  }
};
